import { adminFeedback } from "../../admin_shared/workspaceFeedback.js";

export type OperationalModeRow = {
  id: string;
  modeId: string;
  displayName: string;
  displayNameAr: string | null;
  title: string;
  titleAr: string | null;
  subtitle: string;
  subtitleAr: string | null;
  status: string;
  type: string;
  visibility: string;
  priority: number;
  startsAt: Date | undefined;
  endsAt: Date | undefined;
  updatedAt: Date | undefined;
  updatedBy: string;
  actionShortcutIds: string[];
  prioritizedModuleIds: string[];
  pinnedRefs: Array<{ collection: string; id: string }>;
  emphasizedAnnouncementIds: string[];
  emphasizedEntryIds: string[];
  emphasizedEventIds: string[];
  priorityTags: string[];
  configuration: Record<string, unknown>;
};

export type OperationalModeEditorInput = {
  modeId: string;
  type: string;
  status: "draft" | "scheduled" | "active";
  displayName: string;
  displayNameAr: string;
  title: string;
  titleAr: string;
  subtitle: string;
  subtitleAr: string;
  startsAt: string;
  endsAt: string;
  priority: string;
  visibility: "private" | "connected";
  prioritizedModuleIds: string;
  quickActionIds: string;
  emphasizedEntryIds: string;
};

export type ModeAction = "activate" | "deactivate" | "archive";
type Locale = "en" | "ar";
const esc = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!,
  );
const nullable = (value: string): string | null => value.trim() || null;
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const list = (value: string): string[] => [
  ...new Set(
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
  ),
];

export function canEditMode(mode: Pick<OperationalModeRow, "status">): boolean {
  return ["draft", "scheduled", "active"].includes(mode.status);
}

export function modeActions(status: string): ModeAction[] {
  if (status === "draft") return ["activate", "archive"];
  if (status === "scheduled") return ["activate", "deactivate", "archive"];
  if (status === "active") return ["deactivate", "archive"];
  if (status === "completed") return ["archive"];
  return [];
}

export function modeStatusLabel(status: string, locale: Locale): string {
  const labels: Record<string, [string, string]> = {
    draft: ["Draft", "مسودة"],
    scheduled: ["Scheduled", "مجدول"],
    active: ["Active", "نشط"],
    completed: ["Ended", "منتهٍ"],
    archived: ["Archived", "مؤرشف"],
  };
  return (
    labels[status]?.[locale === "ar" ? 1 : 0] ?? (locale === "ar" ? "غير متاح" : "Unavailable")
  );
}

export function modeFeedback(message: string, locale: Locale): string {
  if (message === "invalid_lifecycle_transition") message = "invalid_mode_transition";
  const text: Record<string, [string, string]> = {
    operations_modes_entitlement_required: [
      "Operational Modes is not enabled for this organization. Contact Shdeed support for help. Changes were not saved.",
      "الأوضاع التشغيلية غير مفعّلة لهذه المؤسسة. تواصل مع دعم شديد للمساعدة. لم يتم حفظ التغييرات.",
    ],
    mode_load_timeout: [
      "Connecting is taking longer than expected. Check your connection and retry.",
      "يستغرق الاتصال وقتاً أطول من المتوقع. تحقق من اتصالك وأعد المحاولة.",
    ],
    mode_load_failed: [
      "Modes could not be loaded. Please retry.",
      "تعذر تحميل الأوضاع. يرجى إعادة المحاولة.",
    ],
    activation_requires_activate_action: [
      "Save a new mode as a draft, then activate it from the list. Your changes are still in this form.",
      "احفظ الوضع الجديد كمسودة ثم فعّله من القائمة. تغييراتك ما زالت في النموذج.",
    ],
    invalid_mode_transition: [
      "This mode's status changed. Close this form and reopen the mode to review its latest state.",
      "تغيرت حالة الوضع. أغلق النموذج وأعد فتح الوضع لمراجعة حالته الحالية.",
    ],
    mode_changed: [
      "This mode was changed elsewhere. Your unsaved text is preserved. Close and reopen it to review the latest version before editing again.",
      "تم تعديل هذا الوضع في مكان آخر. تم الاحتفاظ بالنص غير المحفوظ. أغلقه وأعد فتحه لمراجعة أحدث نسخة.",
    ],
    mode_required: ["Enter a mode name and Home title.", "أدخل اسم الوضع وعنوان الصفحة الرئيسية."],
    mode_dates: [
      "Choose valid dates, with the end after the start.",
      "اختر تواريخ صالحة، بحيث تكون النهاية بعد البداية.",
    ],
    mode_expired: [
      "The end date has passed. Edit the dates before publishing, or end this mode.",
      "انقضى تاريخ النهاية. عدّل التواريخ قبل النشر أو أنهِ هذا الوضع.",
    ],
    mode_priority: [
      "Priority must be a whole number from 0 to 10000.",
      "يجب أن تكون الأولوية عدداً صحيحاً من 0 إلى 10000.",
    ],
    mode_save_failed: [
      "The mode could not be saved. Your changes are still in this form. Try again.",
      "تعذر حفظ الوضع. تغييراتك ما زالت في النموذج. حاول مجدداً.",
    ],
    mode_saved: ["Mode saved.", "تم حفظ الوضع."],
    mode_draft_saved: [
      "Draft saved. Activate it when you are ready to publish.",
      "تم حفظ المسودة. فعّلها عندما تكون جاهزاً للنشر.",
    ],
    mode_published: [
      "Changes saved and published to Mobile Home.",
      "تم حفظ التغييرات ونشرها على الصفحة الرئيسية للتطبيق.",
    ],
    mode_activated: [
      "Mode activated. Any previously active mode has ended.",
      "تم تفعيل الوضع وإنهاء أي وضع نشط سابق.",
    ],
    mode_ended: ["Mode ended.", "تم إنهاء الوضع."],
    mode_archived: ["Mode archived.", "تمت أرشفة الوضع."],
    mode_projection_pending: [
      "The mode was saved, but its Home display could not be confirmed. Check the current Home mode before trying again.",
      "تم حفظ الوضع لكن تعذر تأكيد ظهوره في الصفحة الرئيسية. تحقق من الوضع الحالي قبل المحاولة مجدداً.",
    ],
  };
  const localized = text[message];
  if (localized) return localized[locale === "ar" ? 1 : 0];
  const friendly = adminFeedback(message, locale);
  if (friendly !== message) return friendly;
  if (
    /organization_admin_required|organization_connection_required|entitlement|permission/.test(
      message,
    )
  ) {
    return locale === "ar"
      ? "ليس لديك صلاحية لإدارة الأوضاع في هذه المؤسسة."
      : "Your account does not have access to manage modes for this organization.";
  }
  return locale === "ar"
    ? "تعذر إكمال العملية. لم يتم تأكيد الحفظ. تحقق من الاتصال والصلاحيات وحاول مجدداً."
    : "The operation could not be completed. Saving was not confirmed. Check your connection and permissions, then try again.";
}

// Identity and lifecycle come from the opened record, never from editable fields.
export function buildModeSaveRequest(input: {
  editor: OperationalModeEditorInput;
  original: OperationalModeRow | null;
  organizationId: string;
  newModeId: string;
  now?: number;
}): Record<string, unknown> {
  const { editor, original } = input;
  if (!editor.displayName.trim() || !editor.title.trim()) throw new Error("mode_required");
  if (original && !canEditMode(original)) throw new Error("invalid_mode_transition");
  const date = (value: string): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) throw new Error("mode_dates");
    return parsed.toISOString();
  };
  const startsAt =
    original && editor.startsAt === localModeDate(original.startsAt)
      ? (original.startsAt?.toISOString() ?? null)
      : date(editor.startsAt);
  const endsAt =
    original && editor.endsAt === localModeDate(original.endsAt)
      ? (original.endsAt?.toISOString() ?? null)
      : date(editor.endsAt);
  if (startsAt && endsAt && endsAt <= startsAt) throw new Error("mode_dates");
  const status = original?.status ?? "draft";
  if (status === "active" && endsAt && Date.parse(endsAt) <= (input.now ?? Date.now()))
    throw new Error("mode_expired");
  if (status === "scheduled" && !startsAt) throw new Error("mode_dates");
  const priority = Number(editor.priority);
  if (
    !editor.priority.trim() ||
    !Number.isSafeInteger(priority) ||
    priority < 0 ||
    priority > 10000
  )
    throw new Error("mode_priority");
  const config = original?.configuration ?? {};
  const home = record(config.home);
  return {
    modeId: original?.modeId ?? input.newModeId,
    ...(original
      ? original.updatedAt
        ? { expectedUpdatedAtMillis: original.updatedAt.getTime() }
        : {}
      : { expectedUpdatedAtMillis: null }),
    organizationId: input.organizationId,
    status,
    displayName: { en: editor.displayName.trim(), ar: nullable(editor.displayNameAr) },
    type: editor.type,
    visibility: editor.visibility,
    priority,
    startsAt,
    endsAt,
    activationPolicy: config.activationPolicy ?? "manual",
    featureConfig: config.featureConfig ?? {
      requiredEntitlementIds: ["operations.modes"],
      requiredFeatureFlagIds: [],
    },
    home: {
      ...home,
      heroComponentId: home.heroComponentId ?? "modeHero",
      countdownTargetAt:
        original && editor.endsAt === localModeDate(original.endsAt)
          ? (home.countdownTargetAt ?? null)
          : endsAt,
      actionShortcutIds: list(editor.quickActionIds),
      prioritizedModuleIds: list(editor.prioritizedModuleIds),
      pinnedRefs: original?.pinnedRefs ?? [],
      title: editor.title.trim(),
      titleAr: nullable(editor.titleAr),
      subtitle: editor.subtitle.trim(),
      subtitleAr: nullable(editor.subtitleAr),
    },
    navigation: config.navigation ?? {
      primaryModuleIds: list(editor.prioritizedModuleIds),
      quickActionIds: list(editor.quickActionIds),
    },
    timeline: {
      emphasizedAnnouncementIds: original?.emphasizedAnnouncementIds ?? [],
      emphasizedEntryIds: list(editor.emphasizedEntryIds),
      emphasizedEventIds: original?.emphasizedEventIds ?? [],
      priorityTags: original?.priorityTags ?? [],
    },
  };
}

export function localModeDate(date: Date | undefined): string {
  if (!date) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function renderModeWorkspace(input: {
  locale: Locale;
  modes: OperationalModeRow[];
  editor: OperationalModeEditorInput;
  original: OperationalModeRow | null;
  busy: boolean;
  loading: boolean;
  loadError: string;
  error: string;
  message: string;
  canManage: boolean;
  projection: { modeId: string | null; title: string | null } | undefined;
  pendingAction: { modeId: string; action: ModeAction } | null;
  discardPending: boolean;
}): string {
  const { locale, editor, original } = input;
  const t = (en: string, ar: string): string => (locale === "ar" ? ar : en);
  const name = (mode: OperationalModeRow): string =>
    locale === "ar" ? mode.displayNameAr || mode.displayName : mode.displayName;
  const disabled = input.busy ? "disabled" : "";
  const feedback = (value: string, error = false): string =>
    value
      ? `<p class="${error ? "error" : "status"} status-inline" tabindex="-1" role="${error ? "alert" : "status"}">${esc(modeFeedback(value, locale))}</p>`
      : "";
  const field = (
    key: keyof OperationalModeEditorInput,
    en: string,
    ar: string,
    type = "text",
    required = false,
  ): string =>
    `<label>${t(en, ar)}<input type="${type}" data-operational-mode-editor-field="${key}" value="${esc(editor[key])}" ${key.endsWith("Ar") ? 'dir="rtl"' : ""} ${required ? "required" : ""} ${type === "text" ? 'maxlength="120"' : ""} ${type === "number" ? 'min="0" max="10000" step="1"' : ""}></label>`;
  const actionLabel = (action: ModeAction): string =>
    action === "activate"
      ? t("Activate", "تفعيل")
      : action === "deactivate"
        ? t("End mode", "إنهاء الوضع")
        : t("Archive", "أرشفة");
  const current = input.modes.find((mode) => mode.modeId === input.projection?.modeId);
  const types: Array<[string, string, string]> = [
    ["generic", "General", "عام"],
    ["ramadan", "Ramadan", "رمضان"],
    ["emergencyResponse", "Emergency response", "الاستجابة للطوارئ"],
    ["fundraisingDrive", "Fundraising", "جمع التبرعات"],
    ["schoolRegistration", "School registration", "التسجيل المدرسي"],
    ["majorEvent", "Major event", "حدث رئيسي"],
    ["seasonalCampaign", "Seasonal campaign", "حملة موسمية"],
  ];
  return `<article id="section-operational-modes" class="panel mode-workspace">
    <div class="mode-workspace-heading"><h2>${t("Operational Modes", "الأوضاع التشغيلية")}</h2>
      ${input.canManage ? `<button type="button" class="primary-action" data-mode-create ${disabled} ${input.loading || input.loadError ? "disabled" : ""}>${t("New mode", "وضع جديد")}</button>` : ""}</div>
    ${feedback(input.loadError, true)}${input.loadError ? `<button type="button" data-mode-retry class="secondary-action">${t("Retry", "إعادة المحاولة")}</button>` : ""}
    ${feedback(input.error, true)}${feedback(input.message)}
    <div class="mode-current"><span class="meta">${t("Current Home mode", "الوضع الحالي في الرئيسية")}</span>
      <strong>${esc(input.loading ? t("Loading…", "جارٍ التحميل…") : input.loadError ? t("Unavailable", "غير متاح") : current ? name(current) : input.projection?.title || t("No active mode", "لا يوجد وضع نشط"))}</strong>
      ${current && input.canManage && canEditMode(current) ? `<button type="button" class="secondary-action inline-action" data-operational-mode-edit="${esc(current.modeId)}" ${disabled}>${t("Edit current mode", "تعديل الوضع الحالي")}</button>` : ""}</div>
    ${
      input.loading
        ? `<p role="status">${t("Loading modes…", "جارٍ تحميل الأوضاع…")}</p>`
        : input.loadError
          ? ""
          : input.modes.length === 0
            ? `<p class="meta">${t("No modes yet.", "لا توجد أوضاع بعد.")}</p>`
            : `<ul id="operational-mode-list" class="request-list mode-list">${input.modes
                .map((mode) => {
                  const pending =
                    input.pendingAction?.modeId === mode.modeId ? input.pendingAction : null;
                  const category = types.find((type) => type[0] === mode.type);
                  const actions = input.canManage ? modeActions(mode.status) : [];
                  return `<li data-mode-row="${esc(mode.modeId)}"><div class="mode-row-copy"><strong>${esc(name(mode))}</strong>
        <span class="meta">${esc(category?.[locale === "ar" ? 2 : 1] ?? t("General", "عام"))} · ${mode.visibility === "connected" ? t("Connected people", "الأشخاص المتصلون") : t("Admin only", "للمشرفين فقط")}</span>
        <span>${esc(locale === "ar" ? mode.titleAr || mode.title : mode.title)}</span>
        <small class="meta">${esc(locale === "ar" ? mode.subtitleAr || mode.subtitle : mode.subtitle)}</small></div>
        <div class="mode-row-controls"><span class="status-pill ${mode.status === "active" ? "status-accepted" : "status-pending"}">${modeStatusLabel(mode.status, locale)}</span>
          <div class="button-row">${input.canManage && canEditMode(mode) ? `<button class="secondary-action inline-action" type="button" data-operational-mode-edit="${esc(mode.modeId)}" ${disabled}>${t("Edit", "تعديل")}</button>` : ""}
          ${actions.map((action) => `<button type="button" class="${action === "activate" ? "primary-action" : "danger-action"} inline-action" data-operational-mode-action="${action}" data-operational-mode-id="${esc(mode.modeId)}" ${disabled}>${actionLabel(action)}</button>`).join("")}</div></div>
        ${
          pending
            ? `<div class="mode-confirm" role="alert"><p>${pending.action === "activate" ? t("Activate this mode now? Any other active mode will end.", "تفعيل هذا الوضع الآن؟ سيتم إنهاء أي وضع نشط آخر.") : pending.action === "deactivate" ? t("End this mode? It will no longer be active and cannot be reactivated.", "إنهاء هذا الوضع؟ لن يكون نشطاً ولا يمكن إعادة تفعيله.") : t("Archive this mode? It will become read-only and cannot be reactivated.", "أرشفة هذا الوضع؟ سيصبح للقراءة فقط ولا يمكن إعادة تفعيله.")}</p>
          <div class="button-row"><button class="${pending.action === "activate" ? "primary-action" : "danger-action"}" type="button" data-mode-confirm ${disabled}>${actionLabel(pending.action)}</button><button type="button" class="secondary-action" data-mode-cancel-action ${disabled}>${t("Cancel", "إلغاء")}</button></div></div>`
            : ""
        }</li>`;
                })
                .join("")}</ul>`
    }
    ${
      input.canManage
        ? `<form id="operational-mode-editor-form" class="stacked-form mode-editor" data-task-window-launch="manual">
      ${feedback(input.error, true)}
      <fieldset ${disabled}><legend>${original ? t("Edit mode", "تعديل الوضع") : t("New mode", "وضع جديد")}</legend>
      <p class="meta">${original?.status === "active" ? t("Active · Saving publishes changes to Mobile Home.", "نشط · يؤدي الحفظ إلى نشر التغييرات في التطبيق.") : modeStatusLabel(original?.status ?? "draft", locale)}</p>
      <div class="mode-form-grid">${field("displayName", "Mode name", "اسم الوضع", "text", true)}${field("displayNameAr", "Mode name (Arabic)", "اسم الوضع (العربية)")}
      <label>${t("Category", "الفئة")}<select data-operational-mode-editor-field="type">${types.map(([value, en, ar]) => `<option value="${value}" ${editor.type === value ? "selected" : ""}>${t(en, ar)}</option>`).join("")}</select></label>
      <label>${t("Audience", "الجمهور")}<select data-operational-mode-editor-field="visibility"><option value="connected" ${editor.visibility === "connected" ? "selected" : ""}>${t("Connected people", "الأشخاص المتصلون")}</option><option value="private" ${editor.visibility === "private" ? "selected" : ""}>${t("Admin only", "للمشرفين فقط")}</option></select></label></div>
      <h3>${t("Mobile Home", "الصفحة الرئيسية للتطبيق")}</h3>
      <div class="mode-form-grid">${field("title", "Home title", "عنوان الرئيسية", "text", true)}${field("titleAr", "Home title (Arabic)", "عنوان الرئيسية (العربية)")}
      ${(["subtitle", "subtitleAr"] as const).map((key) => `<label>${key === "subtitle" ? t("Home message", "رسالة الرئيسية") : t("Home message (Arabic)", "رسالة الرئيسية (العربية)")}<textarea data-operational-mode-editor-field="${key}" maxlength="280" rows="3" ${key === "subtitleAr" ? 'dir="rtl"' : ""}>${esc(editor[key])}</textarea></label>`).join("")}</div>
      <h3>${t("Dates", "التواريخ")}</h3><p class="meta">${t("Time zone", "المنطقة الزمنية")}: ${esc(Intl.DateTimeFormat().resolvedOptions().timeZone)}</p>
      <div class="mode-form-grid">${field("startsAt", "Starts at (optional)", "البداية (اختياري)", "datetime-local")}${field("endsAt", "Ends at (optional)", "النهاية (اختياري)", "datetime-local")}</div>
      <details><summary>${t("Advanced", "إعدادات متقدمة")}</summary>${field("priority", "Priority", "الأولوية", "number", true)}</details>
      <div class="button-row"><button type="submit">${input.busy ? t("Saving…", "جارٍ الحفظ…") : original?.status === "active" ? t("Save and publish", "حفظ ونشر") : original ? t("Save changes", "حفظ التغييرات") : t("Save draft", "حفظ المسودة")}</button><button type="button" class="secondary-action" data-mode-close>${t("Cancel", "إلغاء")}</button></div></fieldset>
      ${input.discardPending ? `<div class="mode-confirm" role="alert"><p>${t("Discard your unsaved changes?", "تجاهل التغييرات غير المحفوظة؟")}</p><div class="button-row"><button type="button" class="danger-action" data-mode-discard>${t("Discard changes", "تجاهل التغييرات")}</button><button type="button" class="secondary-action" data-mode-keep>${t("Keep editing", "متابعة التعديل")}</button></div></div>` : ""}
    </form>`
        : ""
    }
  </article>`;
}
